import React from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';

const NavContainer = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 70px;
  background: #000000;
  border-top: 1px solid #333333;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 0 8px;
  z-index: 1000;
  
  /* Safe area for iOS */
  padding-bottom: env(safe-area-inset-bottom);
  
  /* Desktop PWA constraint */
  @media (min-width: 768px) {
    max-width: 414px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const NavButton = styled.button<{ $isActive: boolean; $isCenter?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: transparent;
  border: none;
  color: ${props => props.$isActive ? '#a855f7' : '#ffffff'};
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 12px;
  transition: all 0.2s ease;
  flex: 1;
  max-width: ${props => props.$isCenter ? '80px' : 'none'};
  min-height: 56px;
  position: relative;
  
  &:hover {
    background: #333333;
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  ${props => props.$isActive && `
    background: #000000;
    border: 1px solid #333333;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 40px;
      height: 3px;
      background: #a855f7;
      border-radius: 0 0 3px 3px;
    }
  `}
  
  ${props => props.$isCenter && `
    background: #000000;
    border: 1px solid #333333;
    border-radius: 16px;
    min-width: 64px;
    max-width: 64px;
    height: 64px;
    
    ${props.$isActive && `
      border-color: #444444;
    `}
  `}
`;

const NavIcon = styled.img<{ $isCenter?: boolean }>`
  width: ${props => props.$isCenter ? '24px' : '20px'};
  height: ${props => props.$isCenter ? '24px' : '20px'};
  object-fit: contain;
  filter: ${props => props.$isCenter ? 'none' : 'none'};
`;

const NavLabel = styled.span<{ $isCenter?: boolean }>`
  font-size: 10px;
  font-weight: 600;
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: inherit;
  display: ${props => props.$isCenter ? 'none' : 'block'};
`;

interface NavItem {
  path: string;
  icon: string;
  label: string;
  isCenter?: boolean;
}

const navItems: NavItem[] = [
  { path: '/sentry-pwa/tokens', icon: '/sentry-nav-tokens.png', label: 'Tokens' },
  { path: '/sentry-pwa/stats', icon: '/sentry-nav-ecosystem.png', label: 'Ecosystem' },
  { path: '/sentry-pwa/create', icon: '/sentry-nav-create.png', label: 'Create', isCenter: true },
  { path: '/sentry-pwa/wallet', icon: '/sentry-nav-wallet.png', label: 'Wallet' },
  { path: '/sentry-pwa/docs', icon: '/sentry-nav-settings.png', label: 'Settings' }
];

const SentryPWABottomNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (path: string) => {
    navigate(path);
  };

  return (
    <NavContainer>
      {navItems.map((item) => {
        const isActive = location.pathname === item.path ||
          (item.path === '/sentry-pwa/tokens' && location.pathname.startsWith('/sentry-pwa/token/'));
        
        return (
          <NavButton
            key={item.path}
            $isActive={isActive}
            $isCenter={item.isCenter}
            onClick={() => handleNavClick(item.path)}
            aria-label={item.label}
          >
            <NavIcon src={item.icon} alt={item.label} $isCenter={item.isCenter} />
            <NavLabel $isCenter={item.isCenter}>{item.label}</NavLabel>
          </NavButton>
        );
      })}
    </NavContainer>
  );
};

export default SentryPWABottomNav;
